import React, { useState, useEffect, useCallback } from "react"
import { Col, Row, Spinner, Table } from "reactstrap"
import api from "API/Api"
import { usePermissions } from "API/PermissionContext"

const SMSReport = () => {
  const { canExport, MODULE } = usePermissions()
  const MID = MODULE.REPORTS

  const [loading, setLoading] = useState(true)
  const [dashboard, setDashboard] = useState(null)
  const [logs, setLogs] = useState([])
  const [providerStats, setProviderStats] = useState([])
  const [trend, setTrend] = useState([])
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 })
  const [filters, setFilters] = useState({ dateFrom: "", dateTo: "", status: "", sms_type: "", channel: "", mobile: "", provider: "" })
  const [page, setPage] = useState(1)
  const [expandedRow, setExpandedRow] = useState(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const params = { ...filters, per_page: 25, page }
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k] })

      const [dashRes, logsRes, statsRes, trendRes] = await Promise.all([
        api.get("sms-logs/dashboard", { params: filters }),
        api.get("sms-logs", { params }),
        api.get("sms-logs/provider-stats"),
        api.get("sms-logs/daily-trend"),
      ])

      setDashboard(dashRes.data?.data || null)
      const ld = logsRes.data?.data
      setLogs(ld?.data || [])
      setPagination({ current_page: ld?.current_page || 1, last_page: ld?.last_page || 1, total: ld?.total || 0 })
      setProviderStats(statsRes.data?.data || [])
      setTrend(trendRes.data?.data || [])
    } catch (e) { console.error(e) }
    setLoading(false)
  }, [filters, page])

  useEffect(() => { loadData() }, [loadData])

  const exportCSV = async () => {
    try {
      const resp = await api.get("sms-logs/export", { params: filters })
      const data = resp.data?.data || []
      if (!data.length) return
      let csv = "Date,Channel,Provider,Mobile,Recipient,Type,Status,Cost,Parts,Message\n"
      data.forEach(l => {
        csv += [l.created_at, l.channel, l.provider, l.mobile, l.recipient_name || "", l.sms_type, l.status, l.cost, l.sms_parts, `"${(l.message || "").replace(/"/g, '""').replace(/\n/g, " ")}"`].join(",") + "\n"
      })
      const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }))
      a.download = "sms_report_" + new Date().toISOString().slice(0, 10) + ".csv"; a.click()
    } catch (e) { console.error(e) }
  }

  const statusBadge = (s) => {
    const map = { delivered: { bg: "#dcfce7", color: "#166534" }, sent: { bg: "#dbeafe", color: "#1e40af" }, failed: { bg: "#fee2e2", color: "#991b1b" }, queued: { bg: "#fef9c3", color: "#854d0e" }, skipped: { bg: "#f3f4f6", color: "#4b5563" } }
    const st = map[s] || map.queued
    return <span style={{ background: st.bg, color: st.color, padding: "3px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: 600 }}>{(s || "").toUpperCase()}</span>
  }

  const channelBadge = (c) => c === "whatsapp"
    ? <span style={{ background: "#dcfce7", color: "#166534", padding: "3px 8px", borderRadius: "12px", fontSize: "11px" }}><i className="fab fa-whatsapp"></i> WA</span>
    : <span style={{ background: "#dbeafe", color: "#1e40af", padding: "3px 8px", borderRadius: "12px", fontSize: "11px" }}><i className="fas fa-sms"></i> SMS</span>

  const typeLbl = (t) => {
    const m = { purchase_receipt: "Purchase", daily_summary: "Summary", bill_payment: "Bill Pay", vendor_bill: "Vendor", loan_deduction: "Loan", bonus_credit: "Bonus", recovery_notice: "Recovery", custom: "Custom", test: "Test" }
    return m[t] || t
  }

  // Trend bar chart (simple CSS-based)
  const maxTrend = Math.max(...trend.map(t => t.total), 1)

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4><i className="fas fa-chart-pie" style={{ color: "#6366f1", marginRight: "8px" }}></i>SMS & WhatsApp Report</h4></Col>
          <Col md={6} style={{ textAlign: "right" }}>
            {canExport(MID) && <button className="btn btn-success btn-sm" onClick={exportCSV}><i className="fas fa-file-csv"></i> Export CSV</button>}
          </Col>
        </Row>
      </div>

      {loading ? <div style={{ textAlign: "center", padding: "60px" }}><Spinner color="primary" /></div> : (
        <>
          {/* ══ Summary Cards ══ */}
          {dashboard && (
            <Row className="mb-3" style={{ padding: "0 8px" }}>
              {[
                { label: "Total Messages", value: dashboard.total, icon: "fa-paper-plane", color: "#6366f1" },
                { label: "Delivered", value: dashboard.delivered + dashboard.sent, icon: "fa-check-circle", color: "#22c55e", sub: dashboard.delivery_rate + "%" },
                { label: "Failed", value: dashboard.failed, icon: "fa-times-circle", color: "#ef4444" },
                { label: "Pending", value: dashboard.queued, icon: "fa-clock", color: "#f59e0b" },
                { label: "SMS", value: dashboard.sms_count, icon: "fa-sms", color: "#0ea5e9" },
                { label: "WhatsApp", value: dashboard.whatsapp_count, icon: "fab fa-whatsapp", color: "#25D366" },
                { label: "Fallback Used", value: dashboard.fallback_count, icon: "fa-random", color: "#8b5cf6" },
                { label: "Total Cost", value: "₹" + dashboard.total_cost, icon: "fa-rupee-sign", color: "#f97316" },
              ].map((c, i) => (
                <Col key={i} md={3} sm={6} className="mb-2">
                  <div style={{ background: "#fff", borderLeft: `4px solid ${c.color}`, borderRadius: "10px", padding: "14px 16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: "12px" }}>
                    <i className={`fas ${c.icon}`} style={{ fontSize: "22px", color: c.color }}></i>
                    <div>
                      <div style={{ fontSize: "11px", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" }}>{c.label}</div>
                      <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b" }}>{c.value}{c.sub && <span style={{ fontSize: "12px", color: "#6c757d", marginLeft: "4px" }}>{c.sub}</span>}</div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {/* ══ Trend Chart ══ */}
          {trend.length > 0 && (
            <div style={{ background: "#fff", borderRadius: "12px", padding: "20px", marginBottom: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <h6 style={{ fontWeight: "700", marginBottom: "16px", color: "#1e293b" }}>30-Day Trend</h6>
              <div style={{ display: "flex", alignItems: "flex-end", gap: "3px", height: "100px" }}>
                {trend.map((t, i) => (
                  <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }} title={`${t.date}: ${t.total} total (${t.success} ok, ${t.failed} fail)`}>
                    <div style={{ width: "100%", background: `linear-gradient(to top, #6366f1, #818cf8)`, borderRadius: "3px 3px 0 0", height: `${Math.max((t.total / maxTrend) * 80, 2)}px`, transition: "height 0.3s" }}></div>
                    {i % 5 === 0 && <span style={{ fontSize: "9px", color: "#94a3b8", marginTop: "2px" }}>{t.date?.slice(5)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ══ Provider Stats ══ */}
          {providerStats.length > 0 && (
            <div style={{ background: "#fff", borderRadius: "12px", padding: "20px", marginBottom: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <h6 style={{ fontWeight: "700", marginBottom: "12px", color: "#1e293b" }}>Provider Statistics</h6>
              <Table className="table mb-0" size="sm">
                <thead><tr style={{ background: "#f8f9fa" }}><th>Provider</th><th>Channel</th><th>Total</th><th>Delivered</th><th>Sent</th><th>Failed</th><th>Cost</th></tr></thead>
                <tbody>
                  {providerStats.map((p, i) => (
                    <tr key={i}><td style={{ fontWeight: 600 }}>{p.provider}</td><td>{channelBadge(p.channel)}</td><td>{p.total}</td><td style={{ color: "#22c55e" }}>{p.delivered}</td><td style={{ color: "#0ea5e9" }}>{p.sent}</td><td style={{ color: "#ef4444" }}>{p.failed}</td><td>₹{parseFloat(p.total_cost || 0).toFixed(2)}</td></tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* ══ Filters ══ */}
          <div style={{ background: "#f8f9fa", borderRadius: "10px", padding: "12px 16px", marginBottom: "12px", display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
            <input type="date" className="form-control form-control-sm" style={{ width: "140px" }} value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
            <span style={{ color: "#6c757d" }}>to</span>
            <input type="date" className="form-control form-control-sm" style={{ width: "140px" }} value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
            <select className="form-control form-control-sm" style={{ width: "120px" }} value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
              <option value="">All Status</option><option value="delivered">Delivered</option><option value="sent">Sent</option><option value="failed">Failed</option><option value="queued">Queued</option><option value="skipped">Skipped</option>
            </select>
            <select className="form-control form-control-sm" style={{ width: "120px" }} value={filters.sms_type} onChange={e => setFilters(f => ({ ...f, sms_type: e.target.value }))}>
              <option value="">All Types</option><option value="purchase_receipt">Purchase</option><option value="daily_summary">Summary</option><option value="bill_payment">Bill Pay</option><option value="vendor_bill">Vendor</option><option value="loan_deduction">Loan</option><option value="bonus_credit">Bonus</option><option value="recovery_notice">Recovery</option><option value="custom">Custom</option>
            </select>
            <select className="form-control form-control-sm" style={{ width: "110px" }} value={filters.channel} onChange={e => setFilters(f => ({ ...f, channel: e.target.value }))}>
              <option value="">All Channels</option><option value="sms">SMS</option><option value="whatsapp">WhatsApp</option>
            </select>
            <input type="text" className="form-control form-control-sm" style={{ width: "130px" }} placeholder="Mobile..." value={filters.mobile} onChange={e => setFilters(f => ({ ...f, mobile: e.target.value }))} />
            <button className="btn btn-sm btn-outline-danger" onClick={() => { setFilters({ dateFrom: "", dateTo: "", status: "", sms_type: "", channel: "", mobile: "", provider: "" }); setPage(1) }}><i className="fa fa-times"></i></button>
          </div>

          {/* ══ Logs Table ══ */}
          <div style={{ background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <div className="table-responsive">
              <Table className="table mb-0 dairyTbl" size="sm">
                <thead>
                  <tr><th>#</th><th>Date</th><th>Channel</th><th>Recipient</th><th>Mobile</th><th>Type</th><th>Provider</th><th>Status</th><th>Cost</th><th>Parts</th></tr>
                </thead>
                <tbody>
                  {logs.length > 0 ? logs.map((l, i) => (
                    <React.Fragment key={l.id || i}>
                      <tr onClick={() => setExpandedRow(expandedRow === l.id ? null : l.id)} style={{ cursor: "pointer", background: expandedRow === l.id ? "#f0f9ff" : "" }}>
                        <td>{(pagination.current_page - 1) * 25 + i + 1}</td>
                        <td style={{ fontSize: "12px", whiteSpace: "nowrap" }}>{l.created_at?.slice(0, 16).replace("T", " ")}</td>
                        <td>{channelBadge(l.channel)}</td>
                        <td style={{ fontWeight: 500 }}>{l.recipient_name || "-"}</td>
                        <td style={{ fontFamily: "monospace", fontSize: "12px" }}>{l.mobile}</td>
                        <td>{typeLbl(l.sms_type)}</td>
                        <td style={{ fontSize: "12px" }}>{l.provider || "-"}</td>
                        <td>{statusBadge(l.status)}{l.fallback_used && <span style={{ marginLeft: "4px", fontSize: "10px", color: "#8b5cf6" }} title="Fallback used">⚡</span>}</td>
                        <td>₹{parseFloat(l.cost || 0).toFixed(2)}</td>
                        <td>{l.sms_parts}</td>
                      </tr>
                      {expandedRow === l.id && (
                        <tr><td colSpan={10} style={{ background: "#f8f9fa", padding: "12px 20px" }}>
                          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
                            <div style={{ flex: "1 1 400px" }}>
                              <strong style={{ fontSize: "12px", color: "#6c757d" }}>Message:</strong>
                              <pre style={{ margin: "4px 0 0", whiteSpace: "pre-wrap", fontSize: "12px", background: "#fff", padding: "10px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>{l.message}</pre>
                            </div>
                            <div style={{ minWidth: "200px" }}>
                              {l.provider_msg_id && <div style={{ fontSize: "12px", marginBottom: "4px" }}><strong>Msg ID:</strong> {l.provider_msg_id}</div>}
                              {l.error_message && <div style={{ fontSize: "12px", color: "#ef4444", marginBottom: "4px" }}><strong>Error:</strong> {l.error_message}</div>}
                              {l.delivered_at && <div style={{ fontSize: "12px", marginBottom: "4px" }}><strong>Delivered:</strong> {l.delivered_at}</div>}
                              {l.reference_type && <div style={{ fontSize: "12px" }}><strong>Ref:</strong> {l.reference_type}#{l.reference_id}</div>}
                            </div>
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  )) : (
                    <tr><td colSpan={10}><center style={{ padding: "30px", color: "#6c757d" }}>No messages found</center></td></tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {pagination.last_page > 1 && (
              <div style={{ padding: "12px 16px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "13px", color: "#6c757d" }}>Showing page {pagination.current_page} of {pagination.last_page} ({pagination.total} total)</span>
                <div style={{ display: "flex", gap: "4px" }}>
                  <button className="btn btn-sm btn-outline-primary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                  <button className="btn btn-sm btn-outline-primary" disabled={page >= pagination.last_page} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default SMSReport